import React, { Component } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PropTypes from 'prop-types'

import * as StoresActions from '../../actions/stores'
import { EstateList as EstateListDOM } from '../../components/Screens/Estate'
import { areFiltersAtDefaultValue } from '../../reducers/stores'
import FilterForm from './Forms/FilterForm'
import StoreForm from './Store/Forms/Store/StoreForm'
import { Button } from '../../components'
import ConfirmDom from '../../components/Confirm'
import { EstateUtils } from '../../utils'
import { ACL, Routes } from '../../constants'
import { StoreUtils } from '../../utils'
import { withSize } from '../../components/utils'

class EstateList extends Component {
  static propTypes = {
    devicesStatus: PropTypes.shape({}).isRequired,
    fetchStoresPaginated: PropTypes.func.isRequired,
    fetchStoresPaginatedAndDevicesStatus: PropTypes.func.isRequired,
    hasPermissionToAddStore: PropTypes.bool.isRequired,
    isWorking: PropTypes.bool.isRequired,
    pagination: PropTypes.shape({
      currentPage: PropTypes.number,
      to: PropTypes.number,
      total: PropTypes.number,
    }).isRequired,
    selectedEstate: PropTypes.string.isRequired,
    stores: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    changeStoreFilters: PropTypes.func.isRequired,
    changeStoreSearchString: PropTypes.func.isRequired,
    deselectStore: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      mode: PropTypes.oneOf([
        StoresActions.StoresSearchModes.searchByStore.value,
        StoresActions.StoresSearchModes.searchByDevice.value,
      ]).isRequired,
      groupsToFilter: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      ).isRequired,
      statusToFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
      countriesToFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
      chainsToFilter: PropTypes.arrayOf(PropTypes.number),
    }).isRequired,
    search: PropTypes.string.isRequired,
    app: PropTypes.shape({}).isRequired,
    history: PropTypes.shape({}).isRequired,
  };

    constructor(props) {
        super(props)

        this.state = {
            sort: [{ fieldToSort: 'name', sortType: 'ASC' }],
            isClearingFilters: false,
            isFiltersOpen: false,
            showStoreForm: false,
            confirmHeader: '',
            confirmBody: '',
            isConfirmOpen: false,
            onClickAcceptConfirm: () => null,
            selectedStore: '',
        }

        this.fetchStores = this.fetchStores.bind(this)
        this.onChangeInput = this.onChangeInput.bind(this)
        this.onChangeSort = this.onChangeSort.bind(this)

        this.searchTimeout = null
    }

    componentDidMount() {
        const { deselectStore } = this.props;
        deselectStore();
        this.updateSearchStringWithURL(this.props)
        this.search({ withDevicesStatus: true })
    }

    componentDidUpdate(lastProps) {
        const { selectedEstate: lastEstate } = lastProps
        const { selectedEstate: nextEstate } = this.props
        if (lastEstate !== nextEstate) {
            this.fetchStores()
        }

        // Extract the query string from the URL
        const { location: { search: lastSearch } } = lastProps
        const { location: { search: newSearch } } = this.props

        // If the query string has changed, update the search value
        // and execute the search
        if (lastSearch !== newSearch) {
            this.updateSearchStringWithURL(this.props)
            this.search()
        }
    }

    componentWillUnmount() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
        }
    }

    updateSearchStringWithURL = (props) => {
        const { location: { search: query } } = props
        const params = new URLSearchParams(query)
        const searchValue = params.get('search') ?? ''            
        const { changeStoreSearchString } = this.props
        changeStoreSearchString(searchValue)
    }

    onClickStore = (code) => {
        const { history, selectedEstate: estate, search } = this.props

        const isSearchingDevice = new RegExp('^[0-9a-f]{6,12}$', 'i').test(search)

        history.push(
            Routes.buildRoute(
                Routes.ESTATE_STORE,
                { code, estate, search: isSearchingDevice ? search : '' },
            ),
        )
    }

    get stores() {
        const { devicesStatus, stores } = this.props

        return stores.map(({ devices, ...data }) => {
            // Map each device with its online status
            const mappedDevices = devices?.map((dev) => {
                const { id: deviceID } = dev // Device id
                const { [deviceID]: deviceInfo } = devicesStatus // Device item on mapping object

                // If it does not have any information, just continue
                if (!deviceInfo) {
                    return dev
                }

                // The information we need
                const { isOnline, lastContact: last_contact } = deviceInfo

                // Resulting device
                return {
                    ...dev,
                    isOnline,
                    last_contact,
                }
            })

            // Resulting store
            return {
                ...data,
                devices: mappedDevices,
                offline: StoreUtils.calculateOfflineDevicesPercentage(data.devices_count, data.online_devices_count),
            }
        })
    }

    get isFilterActive() {
        const { isClearingFilters } = this.state
        const { filters, search } = this.props
        return !isClearingFilters && !areFiltersAtDefaultValue(filters, search)
    }

    /**
     * On Change search input
     * @param {React.ChangeEvent<HTMLInputElement>} event 
     */
    onChangeInput = ({ target: { value = '' } }) => {
        this.replaceURL(value)
    }

    /**
     * On key press in search input
     * @param {React.KeyboardEvent<HTMLInputElement>} event 
     */
    onKeyPress = (event) => {
        const { stores } = this.props

        // If pressing enter, and there's only one store in the list,
        // navigate to that store
        if (event.key === 'Enter' && stores.length === 1) {
            const { code } = stores[0]
            this.onClickStore(code)
        }
    }

    /**
     * Replace URL with current search value
     * @param {string} search 
     */
    replaceURL(search) {
        const { history, selectedEstate: estate } = this.props

        // Build the path to this same page
        const path = Routes.buildRoute(Routes['ESTATE:LIST'], { estate })

        // Append the query params to the path
        const pathWithQuery = [path, `search=${encodeURI(search)}`].join('?')

        // Replace current URL with the updated one
        history.replace(pathWithQuery)
    }

    // On Change any input value
    onChangeSort(sort) {
        const { sort: prevSort } = this.state;
        const areEqual =
        sort.length === prevSort.length &&
        sort.every(
            (value, index) =>
            value.fieldToSort === prevSort[index].fieldToSort &&
            value.sortType === prevSort[index].sortType
        );
        this.setState({ sort }, () => {
            if (!areEqual) this.search();
        });
    }

    /**
     * Toggles the filter modal depending on the selected mode
     * @param {String} mode `searchByStore` or `searchByDevice`
     * @async
     */
    openCloseFilters = async (mode) => {
        const { filters, changeStoreFilters } = this.props;
        const { isFiltersOpen } = this.state;

        if (isFiltersOpen) {
            return this.setState({ isFiltersOpen: false })
        }

        await changeStoreFilters({
            ...filters,
            mode,
        })
        this.setState({ isFiltersOpen: true })
    }

    clearFilters = () => {
        this.setState({ isClearingFilters: true }, () => {
            const { changeStoreFilters } = this.props
            changeStoreFilters()
            this.replaceURL('')
            this.search()
            this.setState({ isClearingFilters: false })
        })
    }

  saveFilters = async (form) => {
    const { changeStoreFilters } = this.props;
    const {
      migrated,
      mode,
      selectedStatus,
      selectedCountries,
      selectedChains,
      selectedGroups,
      showEmptyStores,
    } = form;
    const filters = {
      migrated,
      mode,
      groupsToFilter: selectedGroups,
      statusToFilter: selectedStatus,
      countriesToFilter: selectedCountries,
      chainsToFilter: selectedChains,
      showEmptyStores,
    };
    await changeStoreFilters(filters);
    this.openCloseFilters();
    this.search();
  };

  buildNextPageFunc() {
    const { fetchStoresPaginated, isWorking, pagination, filters, search } =
      this.props;
    const { sort } = this.state;
    const { currentPage, to, total } = pagination;
    const {
      countriesToFilter,
      chainsToFilter,
      groupsToFilter: selectedGroupsToFilter,
      mode,
      showEmptyStores,
      statusToFilter,
    } = filters;

    if (!isWorking && to < total) {
      return () =>
        fetchStoresPaginated({
          countriesToFilter,
          chainsToFilter,
          mode,
          order: sort.length ? sort[0].fieldToSort : null,
          orderType: sort.length ? sort[0].sortType : null,
          page: currentPage + 1,
          search,
          selectedGroupsToFilter,
          showEmptyStores,
          statusToFilter,
        });
    }

        return null
    }

  /**
   * Fetch stores information and connected devices
   */
  async fetchStores(withDevicesStatus) {
    const {
      fetchStoresPaginated,
      fetchStoresPaginatedAndDevicesStatus,
      search,
      filters,
    } = this.props;
    const { sort } = this.state;
    const {
      migrated,
      mode,
      groupsToFilter: selectedGroupsToFilter,
      statusToFilter,
      countriesToFilter,
      chainsToFilter,
      showEmptyStores,
    } = filters;

    if (withDevicesStatus) {
      await fetchStoresPaginatedAndDevicesStatus({
        search,
        order: sort.length ? sort[0].fieldToSort : null,
        orderType: sort.length ? sort[0].sortType : null,
        page: 0,
        migrated,
        mode,
        selectedGroupsToFilter,
        statusToFilter,
        countriesToFilter,
        chainsToFilter,
        showEmptyStores,
      });
    } else {
      await fetchStoresPaginated({
        search,
        order: sort.length ? sort[0].fieldToSort : null,
        orderType: sort.length ? sort[0].sortType : null,
        page: 0,
        migrated,
        mode,
        selectedGroupsToFilter,
        statusToFilter,
        countriesToFilter,
        chainsToFilter,
        showEmptyStores,
      });
    }
  }

    search({ withDevicesStatus = false } = {}) {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
        }

        this.searchTimeout = setTimeout(() => {
            this.fetchStores(withDevicesStatus)
        }, 1000)
    }

    openCloseStoreForm = (storeCode, action) => {
        const { showStoreForm } = this.state
        let code = storeCode
        let onAccept = action
        if (storeCode !== String) code = null
        if (!action) onAccept = () => null
        this.setState({
            showStoreForm: !showStoreForm,
            selectedStore: code,
            onClickAcceptConfirm: onAccept,
        })
    }

    openCloseConfirm = (action, confirmHeader, confirmBody) => {
        const { isConfirmOpen } = this.state
        this.setState({
            isConfirmOpen: !isConfirmOpen,
            confirmHeader,
            confirmBody,
            onClickAcceptConfirm: action,
        })
    }

    renderStoreForm = () => {
        const { showStoreForm, selectedStore } = this.state
        const index = this.stores.findIndex((store) => store.code === selectedStore)
        return <StoreForm isOpen={showStoreForm} onClickCancel={this.openCloseStoreForm} storeCode={selectedStore} index={index} />
    }

    render() {
        const {
            filters,
            hasPermissionToAddStore,
            isMobile,
            isWorking,
            pagination: { total },
            search,
        } = this.props
        const { mode } = filters
        const { confirmBody, confirmHeader, isConfirmOpen, isFiltersOpen, onClickAcceptConfirm, showStoreForm, sort } = this.state

        const addStoreButton = showStoreForm ? false : this.openCloseStoreForm

        return (
            <EstateListDOM
                addButton={hasPermissionToAddStore ? addStoreButton : false}
                clearFilters={this.clearFilters}
                isFilterActive={this.isFilterActive}
                isLoading={isWorking}
                mode={mode}
                nextPage={this.buildNextPageFunc()}
                onChangeSearch={this.onChangeInput}
                onChangeSort={this.onChangeSort}
                onClickStore={this.onClickStore}
                onKeyPress={this.onKeyPress}
                openCloseFilters={this.openCloseFilters}
                openStoreForm={this.openCloseStoreForm}
                sortable={sort}
                stores={this.stores}
                totalDevices={total}
                totalFilteredDevices={this.stores.length}
                valueSearch={search}
            >
                <FilterForm
                    isOpen={isFiltersOpen}
                    filters={filters}
                    onClickCancel={this.openCloseFilters}
                    saveFilters={this.saveFilters}
                    clearFilters={this.clearFilters}
                    isActiveFilters={this.isFilterActive}
                />
                <ConfirmDom
                    isOpen={isConfirmOpen}
                    header={confirmHeader}
                    content={confirmBody}
                    onClickAccept={onClickAcceptConfirm}
                    onClickCancel={this.openCloseConfirm}
                    isWorking={isWorking}
                />
                {!isMobile && (
                    <Button
                        theme={'forestGreen'}
                        style={{
                            bottom: '2px',
                            height: '36px',
                        }}
                        onClick={this.openCloseStoreForm}
                    >
                        Create Store
                    </Button>
                )}
                {this.renderStoreForm()}
            </EstateListDOM>
        )
    }
}

function mapStateToProps(state) {
    const {
        app,
        app: { selectedEstate },
        stores: { devices: devicesStatus, isWorking, list: stores, pagination, search, filters, totalDevices, totalFilteredDevices },
    } = state

    return {
        app,
        devicesStatus,
        hasPermissionToAddStore: EstateUtils.hasPermission(state, {
            acls: [ACL.STORE_ADD],
        }),
        isWorking,
        pagination,
        selectedEstate,
        stores,
        search,
        filters,
        totalDevices,
        totalFilteredDevices,
    }
}

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            ...StoresActions,
        },
        dispatch
    )

export default connect(mapStateToProps, mapDispatchToProps)(withSize(EstateList))
